// The landing page's shelf of games: the ones an editor featured, in the
// showcase's order. Each is its lead clip or picture, its title, its line,
// the devices it has packages for, and the three things it offers: Play,
// Download, Remix (caseHtml in src/core/offer.ts).
//
// The server writes the cards into the page and the showcase beside them
// (`#showcase`), so the shelf is there when the page is first drawn. This
// script plays each clip while it is on screen, points the links the page
// itself wrote for one game (`data-game`, `data-game-page`), and in a
// browser that cannot run a game writes the cards again so that each leads
// to the game's page. A page that arrived without the cards (a server that
// could not read the showcase) gets them from GET /api/showcase; until then
// the chapter stays hidden and takes no space.
//
// The page's language is its <html lang>; the showcase answers its words and
// names in it.

import { caseHtml } from "../core/offer.ts";
import type { ShowcaseGame, ShowcaseResponse } from "../core/protocol.ts";
import { lang } from "../ui/lang.ts";

/** The showcase the server wrote into the page, or null for a page without it. */
function written(): ShowcaseGame[] | null {
  try {
    const data = JSON.parse(document.getElementById("showcase")?.textContent ?? "null") as ShowcaseResponse | null;
    return Array.isArray(data?.games) ? data.games : null;
  } catch {
    return null;
  }
}

export function fillShelf(): void {
  const shelf = document.querySelector<HTMLElement>(".shelf");
  const list = shelf?.querySelector<HTMLElement>(".cases");
  if (!shelf || !list) return;

  // A visitor who asked for less motion, or to save data, sees each clip's poster.
  const still = matchMedia("(prefers-reduced-motion: reduce)").matches || (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  // A clip plays while half of it is on screen and waits behind its poster otherwise.
  const watcher = still
    ? null
    : new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const clip = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) clip.play().catch(() => {});
          else clip.pause();
        }
      }, { threshold: 0.5 });

  // A game's browser version draws with WebGPU. A browser without it gets the game's page, which has its clips and its files.
  const plays = "gpu" in navigator;
  const now = lang();
  const pageOf = (game: ShowcaseGame): string => {
    const page = new URL(game.page, location.href);
    page.searchParams.set("from", "landing");
    return page.href;
  };

  /** The shelf with `games` on it. `cards` says the page already has their cards. */
  const settle = (games: ShowcaseGame[], cards: boolean): void => {
    // The links the page wrote for one game by its label (`data-game`, `data-game-page`) go where the showcase says that game is.
    for (const link of document.querySelectorAll<HTMLAnchorElement>("a[data-game], a[data-game-page]")) {
      const game = games.find((each) => each.slug === (link.dataset.game ?? link.dataset.gamePage));
      if (!game) continue;
      if (link.dataset.game) {
        const play = game.browser && plays;
        link.href = play ? game.url : pageOf(game);
        link.dataset.to = play ? "play" : "page";
      } else link.href = `${pageOf(game)}#download`;
      link.dataset.app = game.id;
    }
    if (games.length === 0) return;
    // The server wrote each card for a browser that runs the game. This one writes them where there are none, and where it cannot.
    if (!cards || !plays) {
      for (const card of list.querySelectorAll("li.case[data-slug]")) card.remove();
      const fresh = document.createElement("template");
      fresh.innerHTML = games.map((game) => caseHtml(game, now, plays)).join("");
      // The page's own cards, the mods, stay after the games.
      list.insertBefore(fresh.content, list.firstElementChild);
    }
    if (watcher) for (const clip of list.querySelectorAll<HTMLVideoElement>("li.case[data-slug] video")) watcher.observe(clip);
    shelf.hidden = false;
  };

  const games = written();
  if (games) {
    settle(games, list.querySelector("li.case[data-slug]") !== null);
    return;
  }
  fetch(`/api/showcase${now === "en" ? "" : `?lang=${now}`}`, { credentials: "omit" })
    .then((response) => (response.ok ? (response.json() as Promise<ShowcaseResponse>) : null))
    .then((data) => settle(Array.isArray(data?.games) ? data.games : [], false))
    .catch(() => {});
}
